import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Formik, Form, Field } from 'formik';
import styled from 'styled-components';
import { getPhoneInputMask, correctShowingTypeLabel } from 'utils';
import { Tag, theme, ContactMethodsSelector } from 'components';
import { InviteBuyer } from '../../../../Router/components';
import styles from './appointment_book.module.scss';

const Step1ExtMainForm = ({
  appointment,
  coopUserOrg,
  isMls,
  showCaptcha,
  appointmentTypes,
  listing,
  updateState,
  selectNotifyMethod,
  isBookingVirtualTour,
  extAuth,
  hasMobileNumber,
  setHasMobileNumber,
  showingOverrideType,
  inviteBuyers,
  setInviteBuyers,
  $rootScope,
}) => {
  const phoneRef = useRef();

  const [showNote, setShowNote] = useState(false);

  const initialValues = {
    phone: (appointment && appointment.coop && appointment.coop.phone) || '',
    notifyEmail:
      (appointment && appointment.notify && appointment.notify.email) || '',
    notifyOffice:
      (appointment && appointment.notify && appointment.notify.email) || '',
    email: (appointment.coop && appointment.coop.email) || '',
    showingType:
      showingOverrideType ||
      (appointment && appointment.type) ||
      appointmentTypes[0],
    note: '',
  };

  const validate = values => {
    if (isMls && !values.phone && !extAuth?.hasFeature('SHOWING_AGENT_SMS')) {
      setHasMobileNumber(false);
    } else {
      setHasMobileNumber(true);
    }
    updateState(draft => {
      draft.appointment.coop.phone = values.phone;
      draft.appointment.coop.email = values.email
        ? values.email
        : appointment.coop.email;
      draft.appointment.type = values.showingType;
      draft.appointment.note = values.notes;
    });
  };

  useEffect(() => {
    validate(initialValues);
  }, []);

  useEffect(() => {
    if (phoneRef.current) {
      getPhoneInputMask().mask(phoneRef.current);
    }
  }, [phoneRef.current]);

  const showMobileField = useMemo(() => (
      (extAuth && !extAuth?.hasFeature('SHOWING_AGENT_SMS')) ||
      (appointment?.coop?.orgFeatures &&
        !appointment.coop.orgFeatures.includes('SHOWING_AGENT_SMS')) ||
      isBookingVirtualTour
    ), [extAuth, appointment, isBookingVirtualTour]);

  const showSMSField = useMemo(() => (
      (extAuth?.hasFeature('SHOWING_AGENT_SMS') && appointment?.coop.text) ||
      (appointment?.coop?.orgFeatures?.includes('SHOWING_AGENT_SMS') &&
        appointment?.coop.phone)
    ), [extAuth, appointment]);

  const selectedUser = useMemo(() => {
    const { emails, email, text, phone } = appointment?.coop;

    // Booking for self
    const { auth } = $rootScope;
    const selfBookShowingDefault =
      auth && auth.id && auth.notifications.officeMessages.bookShowingDefault;

    // External BB User
    const externalAuthNotifications = extAuth?.user?.notifications;
    const extAuthBookShowingDefault =
      externalAuthNotifications &&
      extAuth.user.notifications.officeMessages.bookShowingDefault;

    return {
      email: (emails && extAuth && emails[1]) || email,
      phone: text || phone,
      officePhone: coopUserOrg?.phone,
      bookShowingDefault: selfBookShowingDefault || extAuthBookShowingDefault,
    };
  }, [extAuth, appointment, coopUserOrg, $rootScope]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validate={values => validate(values)}
    >
      {() => (
        <Form>
          <div className="form-group">
            <div className="form-group">
              <label>Name:</label>
              {appointment?.coop?.name}
            </div>
            <div className="form-group">
              <h5>{coopUserOrg?.name}</h5>
              {coopUserOrg?.address}
              <br />
            </div>
            <div className={styles.invisibleOnMobile}>
              {/* Hide the Mobile phone selector when org has SHOWING_AGENT_SMS flag enabled but show for virtual tour */}
              {showMobileField && (
                <div className="form-group">
                  <label>Mobile:</label>
                  <Field
                    name="phone"
                    type="text"
                    className="form-control"
                    required={isMls}
                    placeholder="Phone"
                    disabled={showCaptcha}
                    data-cy="appointment-phone"
                    innerRef={phoneRef}
                  />
                  {!hasMobileNumber && (
                    <label className="error">
                      <span className="error">Phone is required</span>
                    </label>
                  )}
                </div>
              )}
              {isBookingVirtualTour && (
                <div>
                  <label>Email: </label>
                  <span style={{ paddingLeft: '5px' }}>
                    {appointment?.coop?.email || ''}
                  </span>
                </div>
              )}
              {!isBookingVirtualTour && (
                <>
                  <div className="form-group">
                    <label>Send Instruction By</label>
                    <ContactMethodsSelector
                      selectedUser={selectedUser}
                      selectNotifyMethod={selectNotifyMethod}
                      hasSmsFeature={showSMSField}
                    />
                  </div>

                  <div className="form-group">
                    <label>Showing Type</label>
                    <Field
                      name="showingType"
                      component="select"
                      className="form-control"
                      disabled={showCaptcha || showingOverrideType}
                      data-cy="showing-type"
                    >
                      {appointmentTypes.map(at => (
                        <option key={at} value={at}>
                          {correctShowingTypeLabel(at)}
                        </option>
                      ))}
                    </Field>
                  </div>

                  {showNote ? (
                    <div className="form-group">
                      <label>Notes</label>
                      <Field
                        name="notes"
                        component="textarea"
                        className="form-control"
                        rows="6"
                        disabled={showCaptcha}
                        data-cy="input-note"
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowNote(true)}
                      className="btn btn-default"
                      disabled={showCaptcha}
                      data-cy="add-note"
                    >
                      Add Note
                    </button>
                  )}
                </>
              )}
            </div>
            {!extAuth && (
              <StyledContainer>
                <label>
                  Invite Buyers <StyleTag color="blue1">New</StyleTag>
                </label>
                <InviteBuyer
                  agent={appointment.coop}
                  value={inviteBuyers}
                  onValueChange={setInviteBuyers}
                  listing={listing}
                  $rootScope={$rootScope}
                />
              </StyledContainer>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Step1ExtMainForm;

const StyledContainer = styled.div`
  margin-top: 15px;
`;

const StyleTag = styled(Tag)`
  border: 1px solid ${theme.blue6};
  color: ${theme.blue6};
  border-radius: 2px;
  margin-left: 5px;
`;
